import React, { useEffect, useRef, useState } from 'react';
import WaveTrail from './WaveTrail';
import AnimatedLogo from './AnimatedLogo';
import type { LiveServerMessage, Session } from '@google/genai';
import {
  ArrowRight,
  ArrowUpRight,
  CalendarCheck,
  Check,
  ChevronDown,
  Clock3,
  AlertCircle,
  Loader2,
  Menu,
  MessageSquareText,
  Mic,
  Phone,
  PhoneCall,
  PhoneOff,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';

type TranscriptLine = {
  speaker: 'Caller' | 'Agent';
  text: string;
};

type LiveTranscript = {
  caller: string;
  agent: string;
  callerTurn: number;
  agentTurn: number;
};

type SchedulerReceipt = {
  id: number;
  phase: 'checking' | 'found' | 'saving' | 'saved';
  outcome: string;
  status: string;
};

const emptyLiveTranscript = (): LiveTranscript => ({
  caller: '',
  agent: '',
  callerTurn: 0,
  agentTurn: 0,
});

type Agent = {
  id: string;
  name: string;
  role: string;
  specialty: string;
  industry: string;
  title: string;
  description: string;
  outcome: string;
  image: string;
  thumbnail: string;
  alt: string;
  voiceName: string;
  transcript: TranscriptLine[];
};

const agents: Agent[] = [
  {
    id: 'restaurant',
    name: 'Mara',
    role: 'Restaurant host',
    specialty: 'Reservations',
    industry: 'Restaurant',
    title: 'Friday night, handled.',
    description: 'Mara checks availability, answers menu questions, captures dietary notes, and confirms the table before the host reaches for the phone.',
    outcome: 'Table for 4 confirmed',
    image: '/agent-mara.webp',
    thumbnail: '/agent-mara-thumb.webp',
    alt: 'Mara, the restaurant reservation voice agent',
    voiceName: 'Kore',
    transcript: [
      { speaker: 'Caller', text: 'Could I book for four this Friday around 7:30?' },
      { speaker: 'Agent', text: 'I have 7:15 or 7:45. Which works better?' },
      { speaker: 'Caller', text: '7:45, please. One guest is gluten-free.' },
      { speaker: 'Agent', text: 'Done. I’ve added the dietary note for your table.' },
    ],
  },
  {
    id: 'retail',
    name: 'Ana',
    role: 'Retail support specialist',
    specialty: 'Retail support',
    industry: 'Retail',
    title: 'Every product question, answered.',
    description: 'Ana checks stock, explains return policies, and handles product questions without making a shopper wait for the store team.',
    outcome: 'Product enquiry resolved',
    image: '/agent-ana.webp',
    thumbnail: '/agent-ana-thumb.webp',
    alt: 'Ana, the retail support voice agent',
    voiceName: 'Puck',
    transcript: [
      { speaker: 'Caller', text: 'Do you have a NovaBook Pro available for pickup today?' },
      { speaker: 'Agent', text: 'I’ll check live inventory. Do you prefer black or silver?' },
      { speaker: 'Caller', text: 'Silver, please.' },
      { speaker: 'Agent', text: 'I found one available for pickup today. Would you like me to reserve it?' },
      { speaker: 'Caller', text: 'Yes, under Jordan Lee.' },
      { speaker: 'Agent', text: 'Done. The NovaBook Pro is held under Jordan Lee until 8 PM, and I’ve texted the pickup details.' },
    ],
  },
  {
    id: 'realestate',
    name: 'Theo',
    role: 'Property leasing agent',
    specialty: 'Property viewings',
    industry: 'Property',
    title: 'A viewing booked before the lead cools.',
    description: 'Theo answers availability and amenity questions, qualifies prospective tenants, and schedules viewings while interest is high.',
    outcome: 'Two-bedroom viewing scheduled',
    image: '/agent-theo.webp',
    thumbnail: '/agent-theo-thumb.webp',
    alt: 'Theo, the real estate leasing voice agent',
    voiceName: 'Fenrir',
    transcript: [
      { speaker: 'Caller', text: 'I’d like to see a two-bedroom in the Downtown Tower.' },
      { speaker: 'Agent', text: 'I can help with that. Are weekday afternoons convenient?' },
      { speaker: 'Caller', text: 'Thursday after 4 PM would be ideal.' },
      { speaker: 'Agent', text: 'I have 4:30 PM available. Shall I reserve it?' },
    ],
  },
  {
    id: 'healthcare',
    name: 'Lucia',
    role: 'Clinic receptionist',
    specialty: 'Patient access',
    industry: 'Healthcare',
    title: 'Every patient reaches the right next step.',
    description: 'Lucia schedules appointments, answers approved clinic questions, collects essential details, and routes urgent or sensitive calls to your care team.',
    outcome: 'New-patient visit scheduled',
    image: '/agent-lucia.webp',
    thumbnail: '/agent-lucia-thumb.webp',
    alt: 'Lucia, the healthcare clinic voice agent',
    voiceName: 'Aoede',
    transcript: [
      { speaker: 'Caller', text: 'I need to book a first appointment with a family doctor.' },
      { speaker: 'Agent', text: 'I can help with that. Is Tuesday morning or Wednesday afternoon better?' },
      { speaker: 'Caller', text: 'Wednesday afternoon, please.' },
      { speaker: 'Agent', text: 'You’re scheduled for Wednesday at 3:30 PM. I’ll text the new-patient instructions now.' },
    ],
  },
];

const faqs = [
  {
    question: 'What is an AI voice receptionist for business?',
    answer: 'It is a conversational phone agent trained on your hours, services, policies, and workflows. It answers natural questions, handles routine calls, and can complete tasks such as capturing reservations, checking stock, or scheduling appointments.',
  },
  {
    question: 'Can RESERVOLUTION use our existing phone number?',
    answer: 'Yes. The service is designed to work with your current call flow, including overflow and after-hours routing, so guests can keep calling the number they already know.',
  },
  {
    question: 'Can the agent work with our booking or business systems?',
    answer: 'RESERVOLUTION can be configured around the reservation, calendar, inventory, and customer tools your team already uses. The exact workflow is mapped during setup so confirmations and staff visibility remain consistent.',
  },
  {
    question: 'What happens when a caller needs a person?',
    answer: 'You define the handoff rules. Sensitive requests, VIP callers, complaints, or anything outside the agent’s approved scope can be transferred or sent to the right team member with context.',
  },
  {
    question: 'Does it support multiple languages?',
    answer: 'Yes. Multilingual agents can be configured for the languages your customers use, with transcripts available so your team can review what happened without listening to every call.',
  },
  {
    question: 'How much does a general voice agent cost?',
    answer: 'Founding-customer pricing starts at €99 per month plus €0.25 per connected minute for Voice Receptionist, or €249 plus €0.25 per connected minute for Voice Operations. The offer is limited to the first 10 customers and the monthly price is locked for 12 months. Enterprise plans start at €599; regulated healthcare is quoted separately.',
  },
  {
    question: 'Does AI replace our team?',
    answer: 'No. It removes repetitive phone interruptions and covers moments when staff cannot answer. Your team keeps control of escalation and focuses on the customers and work that need human judgment.',
  },
];

function SignalBars({ count = 42 }: { count?: number }) {
  return (
    <div className="signal-bars" aria-hidden="true">
      {Array.from({ length: count }).map((_, index) => (
        <i key={index} style={{ height: `${16 + ((index * 19) % 66)}%` }} />
      ))}
    </div>
  );
}

const ContactForm: React.FC<{ context?: 'general' | 'restaurant' }> = ({ context = 'general' }) => {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [feedback, setFeedback] = useState('');
  const isRestaurant = context === 'restaurant';

  const submitEnquiry = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('sending');
    setFeedback('');

    const form = event.currentTarget;
    const payload = Object.fromEntries(new FormData(form).entries());
    if (isRestaurant && typeof payload.intent === 'string') {
      const message = typeof payload.message === 'string' ? payload.message.trim() : '';
      payload.message = `Preferred next step: ${payload.intent}\n\n${message}`.trim();
      delete payload.intent;
    }

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json() as { error?: string };
      if (!response.ok) throw new Error(result.error || 'We could not send your enquiry.');

      form.reset();
      setStatus('success');
      setFeedback(isRestaurant
        ? 'Thanks. Your restaurant enquiry is in, and we will be in touch.'
        : 'Thanks. Your enquiry is in, and we will be in touch.');
    } catch (error) {
      setStatus('error');
      setFeedback(error instanceof Error ? error.message : 'We could not send your enquiry. Please try again.');
    }
  };

  return (
    <section className={`contact-section${isRestaurant ? ' restaurant-contact' : ''}`} id="contact" aria-labelledby="contact-heading">
      <div className="contact-intro">
        <span className="section-index">{isRestaurant ? 'Your next step' : '08 / Start with one call flow'}</span>
        <h2 id="contact-heading">{isRestaurant ? 'Start with your restaurant.' : 'Where does the phone slow you down?'}</h2>
        <p>{isRestaurant
          ? 'Choose how you want to begin. You can configure the restaurant and test bookings yourself; our team currently helps connect the live phone number.'
          : 'Tell us what callers need and where your team gets interrupted. We will map the first useful call flow around your business.'}</p>
        <div className="contact-promise">
          {isRestaurant ? <CalendarCheck size={21} /> : <PhoneCall size={21} />}
          <span>
            <strong>{isRestaurant ? 'Built around your floor and phone flow' : 'A practical first conversation'}</strong>
            <small>{isRestaurant ? 'Your hours, table rules, booking system, and moments that still need a host.' : 'Your calls, systems, and handoff rules. No generic sales script.'}</small>
          </span>
        </div>
      </div>

      {status === 'success' ? (
        <div className="contact-success" role="status">
          <span><Check size={24} /></span>
          <h3>Message received.</h3>
          <p>{feedback}</p>
          <button type="button" onClick={() => setStatus('idle')}>Send another enquiry</button>
        </div>
      ) : (
        <form className="contact-form" onSubmit={submitEnquiry} aria-busy={status === 'sending'}>
          <div className="form-row">
            <label>Name<input name="name" autoComplete="name" minLength={2} maxLength={100} required /></label>
            <label>Work email<input name="email" type="email" autoComplete="email" maxLength={160} required /></label>
          </div>
          <div className="form-row">
            <label>Company<input name="company" autoComplete="organization" minLength={2} maxLength={120} required /></label>
            {isRestaurant ? (
              <><input name="industry" type="hidden" value="Restaurant" /><label>Next step<select name="intent" defaultValue="Start setup" required><option>Start setup</option><option>Book a demo</option><option>Check compatibility</option></select></label></>
            ) : (
              <label>Industry<select name="industry" defaultValue="" required><option value="" disabled>Choose one</option><option>Restaurant</option><option>Retail</option><option>Property</option><option>Healthcare</option><option>Other</option></select></label>
            )}
          </div>
          <label>{isRestaurant ? 'Tell us about your current setup' : 'What would you like the agent to handle?'}<textarea name="message" rows={5} minLength={10} maxLength={2000} placeholder={isRestaurant ? 'For example: we use an existing booking system, take most reservations by phone, and need help during dinner service.' : 'For example: take reservations after 6 PM, answer menu questions, and transfer VIP callers.'} required /></label>
          <label className="contact-honeypot" aria-hidden="true">Website<input name="website" tabIndex={-1} autoComplete="off" /></label>
          <div className="form-submit-row">
            <p>We will only use these details to respond to your enquiry.</p>
            <button className="button contact-submit" type="submit" disabled={status === 'sending'}>
              {status === 'sending' ? <Loader2 className="spin" size={18} /> : <ArrowUpRight size={18} />}
              {status === 'sending' ? 'Sending' : isRestaurant ? 'Continue with my restaurant' : 'Plan my call flow'}
            </button>
          </div>
          {status === 'error' && <p className="contact-feedback" role="alert"><AlertCircle size={17} /> {feedback}</p>}
        </form>
      )}
    </section>
  );
};

function VoiceWaveform({ analyser, active, preview = false, onBlue = false }: { analyser: AnalyserNode | null; active: boolean; preview?: boolean; onBlue?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    let frame = 0;
    let width = 0;
    let height = 0;
    let phase = 0;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let energy = 0;
    const samples = new Uint8Array(analyser?.fftSize || 512);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      canvas.width = Math.round(width * pixelRatio);
      canvas.height = Math.round(height * pixelRatio);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    };

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    const drawRibbon = (values: number[], amplitude: number, lineWidth: number, alpha: number) => {
      const gradient = context.createLinearGradient(0, 0, width, 0);
      if (onBlue) {
        gradient.addColorStop(0, `rgba(11, 15, 20, ${alpha * .08})`);
        gradient.addColorStop(.24, `rgba(11, 15, 20, ${alpha * .72})`);
        gradient.addColorStop(.62, `rgba(255, 218, 145, ${alpha})`);
        gradient.addColorStop(1, `rgba(11, 15, 20, ${alpha * .08})`);
      } else {
        gradient.addColorStop(0, `rgba(47, 187, 238, ${alpha * .18})`);
        gradient.addColorStop(.22, `rgba(47, 187, 238, ${alpha})`);
        gradient.addColorStop(.62, `rgba(211, 166, 87, ${alpha})`);
        gradient.addColorStop(1, `rgba(47, 187, 238, ${alpha * .14})`);
      }
      context.beginPath();
      values.forEach((value, index) => {
        const progress = index / (values.length - 1);
        const x = progress * width;
        const edgeFade = Math.sin(progress * Math.PI);
        const y = height / 2 + value * height * amplitude * edgeFade;
        if (index === 0) context.moveTo(x, y);
        else context.lineTo(x, y);
      });
      context.strokeStyle = gradient;
      context.lineWidth = lineWidth;
      context.lineCap = 'round';
      context.lineJoin = 'round';
      context.stroke();
    };

    const render = () => {
      phase += active ? .046 : .018;
      if (active && analyser) analyser.getByteTimeDomainData(samples);

      let rawEnergy = 0;
      const pointCount = 72;
      const values = Array.from({ length: pointCount }, (_, index) => {
        if (active && analyser) {
          const sampleIndex = Math.floor((index / (pointCount - 1)) * (samples.length - 1));
          const value = (samples[sampleIndex] - 128) / 128;
          rawEnergy += value * value;
          return value;
        }
        return (Math.sin(index * .34 + phase) * .034 + Math.sin(index * .12 - phase * .7) * .016) * (preview ? 5 : 1);
      });

      rawEnergy = Math.sqrt(rawEnergy / pointCount);
      energy += (rawEnergy - energy) * .18;
      const idleLift = active ? .015 : .038;
      const visibleValues = values.map((value, index) => {
        const shimmer = Math.sin(index * .46 + phase * 1.35) * idleLift;
        return value + shimmer * Math.max(.15, 1 - energy * 4);
      });

      context.clearRect(0, 0, width, height);
      context.globalCompositeOperation = 'lighter';
      drawRibbon(visibleValues, .64, 12 + energy * 20, .13 + Math.min(energy * 2, .2));
      drawRibbon(visibleValues, .59, 4.5, .74);
      drawRibbon(visibleValues, .55, 1.35, 1);
      context.globalCompositeOperation = 'source-over';
      if (!reducedMotion) frame = requestAnimationFrame(render);
    };

    frame = requestAnimationFrame(render);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [active, analyser, preview, onBlue]);

  return <canvas ref={canvasRef} className="voice-waveform" aria-hidden="true" />;
}

function Capability({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div className="capability" onPointerEnter={(event) => { if (event.pointerType !== 'touch') setHovered(true); }} onPointerLeave={() => setHovered(false)}>
      <div className="capability-signal" aria-hidden="true">
        {hovered && <VoiceWaveform analyser={null} active={false} preview />}
      </div>
      {icon}
      <span className="capability-copy"><strong>{title}</strong>{children}</span>
    </div>
  );
}

const businessToolsFor = (agentId: string) => {
  const fields = agentId === 'restaurant'
    ? {
        properties: {
          date: { type: 'string', description: 'Requested date or day.' },
          preferred_time: { type: 'string', description: 'Requested time or time window.' },
          party_size: { type: 'string', description: 'Number of guests, written as supplied by the caller.' },
        },
        required: ['date', 'preferred_time', 'party_size'],
      }
    : agentId === 'retail'
      ? {
          properties: {
            product: { type: 'string', description: 'Product the caller wants checked.' },
            variant: { type: 'string', description: 'Requested color or variant, if supplied.' },
          },
          required: ['product'],
        }
      : {
          properties: {
            date: { type: 'string', description: 'Requested date or day.' },
            preferred_time: { type: 'string', description: 'Requested time or time window.' },
            appointment_type: { type: 'string', description: 'Viewing or clinic visit type.' },
          },
          required: ['date', 'preferred_time', 'appointment_type'],
        };

  const functionDeclarations: Array<{ name: string; description: string; parametersJsonSchema: Record<string, unknown> }> = [{
    name: 'check_availability',
    description: agentId === 'retail'
      ? 'Check the business inventory system. Call only after the caller identifies the product to check. Never call at the greeting.'
      : 'Check the business scheduler for open times. Call only after the caller provides every required scheduling detail. Never call speculatively or at the greeting.',
    parametersJsonSchema: { type: 'object', ...fields, additionalProperties: false },
  }];

  if (agentId === 'retail') {
    functionDeclarations.push({
      name: 'reserve_item',
      description: 'Reserve an available product in the store system. Call only after check_availability confirms stock, the caller explicitly asks to reserve it, and the caller supplies their name. Never claim the item is reserved before this tool returns.',
      parametersJsonSchema: {
        type: 'object',
        properties: {
          product: { type: 'string', description: 'Available product to reserve.' },
          variant: { type: 'string', description: 'Selected color or variant, if relevant.' },
          customer_name: { type: 'string', description: 'Name for the pickup reservation.' },
        },
        required: ['product', 'customer_name'],
        additionalProperties: false,
      },
    });
  } else {
    functionDeclarations.push({
      name: 'save_schedule',
      description: 'Save the selected time to the business scheduler. Call only after check_availability returned options, the caller explicitly selected one, and the caller supplied their name. Never claim the booking is confirmed before this tool returns.',
      parametersJsonSchema: {
        type: 'object',
        properties: {
          selected_time: { type: 'string', description: 'The available time explicitly selected by the caller.' },
          customer_name: { type: 'string', description: 'Caller name for the reservation or appointment.' },
          notes: { type: 'string', description: 'Optional relevant notes supplied by the caller.' },
        },
        required: ['selected_time', 'customer_name'],
        additionalProperties: false,
      },
    });
  }

  return { functionDeclarations };
};

const simulatedAvailability = {
  restaurant: ['7:15 PM', '7:45 PM'],
  retail: ['Silver in stock', 'Black in stock'],
  realestate: ['4:30 PM', '5:15 PM'],
  healthcare: ['3:30 PM', '4:00 PM'],
} as const;

const HomePage: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [connectionState, setConnectionState] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [liveTranscript, setLiveTranscript] = useState<LiveTranscript>(emptyLiveTranscript());
  const [schedulerReceipt, setSchedulerReceipt] = useState<SchedulerReceipt | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const activeAgent = agents[activeIndex];

  const inputContextRef = useRef<AudioContext | null>(null);
  const outputContextRef = useRef<AudioContext | null>(null);
  const outputAnalyserRef = useRef<AnalyserNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const inputSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const sessionRef = useRef<Promise<Session> | null>(null);
  const sessionGenerationRef = useRef(0);
  const callActiveRef = useRef(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const nextStartTimeRef = useRef(0);
  const outputSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const activeTranscriptSpeakerRef = useRef<'caller' | 'agent' | null>(null);
  const handledSchedulerCallsRef = useRef(new Set<string>());
  const schedulerTimerRef = useRef<number | null>(null);

  const showSchedulerReceipt = (phase: SchedulerReceipt['phase'], outcome: string, status: string) => {
    setSchedulerReceipt({ id: Date.now(), phase, outcome, status });
    if (schedulerTimerRef.current) window.clearTimeout(schedulerTimerRef.current);
    schedulerTimerRef.current = window.setTimeout(() => setSchedulerReceipt(null), phase === 'checking' ? 6200 : 4600);
  };

  const appendTranscript = (speaker: 'caller' | 'agent', text: string) => {
    const startsNewTurn = activeTranscriptSpeakerRef.current !== speaker;
    activeTranscriptSpeakerRef.current = speaker;
    setLiveTranscript((current) => ({
      ...current,
      [speaker]: startsNewTurn ? text : `${current[speaker]}${text}`,
      [`${speaker}Turn`]: startsNewTurn ? current[`${speaker}Turn`] + 1 : current[`${speaker}Turn`],
    }));
  };

  const clearLiveTranscript = () => {
    activeTranscriptSpeakerRef.current = null;
    handledSchedulerCallsRef.current.clear();
    setSchedulerReceipt(null);
    if (schedulerTimerRef.current) window.clearTimeout(schedulerTimerRef.current);
    schedulerTimerRef.current = null;
    setLiveTranscript(emptyLiveTranscript());
  };

  const decodeBase64 = (value: string) => {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return bytes;
  };

  const decodeAudioData = (data: Uint8Array, context: AudioContext, sampleRate: number) => {
    const pcm = new Int16Array(data.buffer);
    const buffer = context.createBuffer(1, pcm.length, sampleRate);
    const channel = buffer.getChannelData(0);
    for (let index = 0; index < pcm.length; index += 1) channel[index] = pcm[index] / 32768;
    return buffer;
  };

  const createAudioBlob = (data: Float32Array) => {
    const pcm = new Int16Array(data.length);
    for (let index = 0; index < data.length; index += 1) {
      const sample = Math.max(-1, Math.min(1, data[index]));
      pcm[index] = sample < 0 ? sample * 32768 : sample * 32767;
    }
    const bytes = new Uint8Array(pcm.buffer);
    let binary = '';
    for (let index = 0; index < bytes.byteLength; index += 1) binary += String.fromCharCode(bytes[index]);
    return { data: btoa(binary), mimeType: 'audio/pcm;rate=16000' };
  };

  const stopSession = async (updateState = true) => {
    sessionGenerationRef.current += 1;
    callActiveRef.current = false;
    processorRef.current?.disconnect();
    processorRef.current = null;
    inputSourceRef.current?.disconnect();
    inputSourceRef.current = null;
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    outputSourcesRef.current.forEach((source) => {
      try { source.stop(); } catch { /* source already stopped */ }
    });
    outputSourcesRef.current.clear();
    outputAnalyserRef.current?.disconnect();
    outputAnalyserRef.current = null;
    nextStartTimeRef.current = 0;

    const session = sessionRef.current;
    sessionRef.current = null;
    if (session) session.then((liveSession) => liveSession.close()).catch(() => undefined);

    const contexts = [inputContextRef.current, outputContextRef.current];
    inputContextRef.current = null;
    outputContextRef.current = null;
    if (updateState) setConnectionState('idle');
    await Promise.all(contexts.map((context) => context && context.state !== 'closed' ? context.close().catch(() => undefined) : undefined));
  };

  useEffect(() => {
    return () => {
      if (schedulerTimerRef.current) window.clearTimeout(schedulerTimerRef.current);
      void stopSession(false);
    };
  }, []);

  const selectAgent = (index: number) => {
    if (index === activeIndex) return;
    void stopSession();
    setErrorMessage('');
    clearLiveTranscript();
    setActiveIndex(index);
  };

  const moveAgent = (direction: number) => {
    selectAgent((activeIndex + direction + agents.length) % agents.length);
  };

  const startSession = async () => {
    if (callActiveRef.current) return;
    callActiveRef.current = true;
    const generation = ++sessionGenerationRef.current;
    const isCurrent = () => generation === sessionGenerationRef.current;
    setConnectionState('connecting');
    setErrorMessage('');
    clearLiveTranscript();

    try {
      // Unlock audio during the tap, before network or permission awaits on mobile.
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass || !navigator.mediaDevices?.getUserMedia) {
        throw new Error('This browser does not support live calls. Try Safari or Chrome.');
      }
      const inputContext = new AudioContextClass({ sampleRate: 16000 }) as AudioContext;
      inputContextRef.current = inputContext;
      const outputContext = new AudioContextClass({ sampleRate: 24000 }) as AudioContext;
      outputContextRef.current = outputContext;
      await Promise.all([inputContext.resume(), outputContext.resume()]);
      if (!isCurrent()) return;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      if (!isCurrent()) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      mediaStreamRef.current = stream;
      const tokenResponse = await fetch('/api/live-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenario: activeAgent.id }),
      });
      const tokenPayload = await tokenResponse.json() as {
        token?: string;
        model?: string;
        voiceName?: string;
        systemInstruction?: string;
        error?: string;
      };
      if (!isCurrent()) return;
      if (!tokenResponse.ok || !tokenPayload.token || !tokenPayload.model) {
        throw new Error(tokenPayload.error || 'Could not start the live voice session.');
      }

      const outputNode = outputContext.createGain();
      const outputAnalyser = outputContext.createAnalyser();
      outputAnalyser.fftSize = 512;
      outputAnalyser.smoothingTimeConstant = .68;
      outputAnalyserRef.current = outputAnalyser;
      outputNode.connect(outputAnalyser);
      outputAnalyser.connect(outputContext.destination);

      const { GoogleGenAI, Modality } = await import('@google/genai');
      if (!isCurrent()) return;
      const ai = new GoogleGenAI({ apiKey: tokenPayload.token, httpOptions: { apiVersion: 'v1alpha' } });
      const sessionPromise = ai.live.connect({
        model: tokenPayload.model,
        callbacks: {
          onmessage: (message: LiveServerMessage) => {
            if (!isCurrent()) return;
            const serverContent = message.serverContent;
            const callerText = serverContent?.inputTranscription?.text;
            const agentText = serverContent?.outputTranscription?.text;
            if (callerText) appendTranscript('caller', callerText);
            if (agentText) appendTranscript('agent', agentText);

            for (const functionCall of message.toolCall?.functionCalls || []) {
              if (!['check_availability', 'save_schedule', 'reserve_item'].includes(functionCall.name || '') || functionCall.willContinue === true) continue;
              const callKey = functionCall.id || `${functionCall.name}:${JSON.stringify(functionCall.args || {})}`;
              if (handledSchedulerCallsRef.current.has(callKey)) continue;
              handledSchedulerCallsRef.current.add(callKey);

              const respond = (response: Record<string, unknown>) => {
                sessionPromise.then((liveSession) => liveSession.sendToolResponse({
                  functionResponses: {
                    ...(functionCall.id ? { id: functionCall.id } : {}),
                    name: functionCall.name,
                    response,
                  },
                })).catch(() => {
                  if (!isCurrent()) return;
                  setConnectionState('error');
                  setErrorMessage('The demo business system did not respond. Please end the call and try again.');
                });
              };

              if (functionCall.name === 'save_schedule') {
                showSchedulerReceipt('saving', 'Saving selection', 'Writing to scheduler');
                respond({ saved: true, confirmation: `DEMO-${Date.now().toString().slice(-6)}`, source: 'demo_business_scheduler' });
                window.setTimeout(() => {
                  if (isCurrent()) showSchedulerReceipt('saved', activeAgent.outcome, 'Saved to scheduler');
                }, 550);
                continue;
              }

              if (functionCall.name === 'reserve_item') {
                showSchedulerReceipt('saving', 'Reserving item', 'Writing to store system');
                respond({ reserved: true, confirmation: `DEMO-${Date.now().toString().slice(-6)}`, source: 'demo_store_system' });
                window.setTimeout(() => {
                  if (isCurrent()) showSchedulerReceipt('saved', 'Pickup reservation saved', 'Reserved in store system');
                }, 550);
                continue;
              }

              const isInventory = activeAgent.id === 'retail';
              showSchedulerReceipt('checking', isInventory ? 'Checking inventory' : 'Checking calendar', isInventory ? 'Searching live stock' : 'Searching approved times');
              const slots = simulatedAvailability[activeAgent.id];
              respond({ available: true, options: slots, source: isInventory ? 'demo_inventory_system' : 'demo_business_scheduler' });
              window.setTimeout(() => {
                if (isCurrent()) showSchedulerReceipt('found', isInventory ? 'Stock found' : 'Availability found', isInventory ? 'Available variants ready' : 'Open times ready');
              }, 650);
            }

            for (const part of serverContent?.modelTurn?.parts || []) {
              const audioData = part.inlineData?.data;
              if (!audioData || !outputContextRef.current) continue;
              const context = outputContextRef.current;
              nextStartTimeRef.current = Math.max(nextStartTimeRef.current, context.currentTime);
              const audioBuffer = decodeAudioData(decodeBase64(audioData), context, 24000);
              const source = context.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(outputNode);
              source.addEventListener('ended', () => outputSourcesRef.current.delete(source));
              source.start(nextStartTimeRef.current);
              nextStartTimeRef.current += audioBuffer.duration;
              outputSourcesRef.current.add(source);
            }

            if (serverContent?.interrupted) {
              outputSourcesRef.current.forEach((source) => {
                try { source.stop(); } catch { /* source already stopped */ }
              });
              outputSourcesRef.current.clear();
              nextStartTimeRef.current = 0;
            }
          },
          onerror: () => {
            if (!isCurrent()) return;
            void stopSession(false);
            setConnectionState('error');
            setErrorMessage('The Gemini Live connection was interrupted. Please try again.');
          },
          onclose: (event) => {
            if (!isCurrent()) return;
            void stopSession(false);
            setConnectionState('error');
            const reason = event.reason?.trim();
            setErrorMessage(reason ? `The live call ended: ${reason}` : `The live call ended unexpectedly (code ${event.code}). Please try again.`);
          },
        },
        config: {
          responseModalities: [Modality.AUDIO],
          tools: [businessToolsFor(activeAgent.id)],
          systemInstruction: tokenPayload.systemInstruction,
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: tokenPayload.voiceName || activeAgent.voiceName } },
          },
          inputAudioTranscription: {},
          outputAudioTranscription: {},
        },
      });
      sessionRef.current = sessionPromise;
      const session = await sessionPromise;
      if (!isCurrent()) { session.close(); return; }
      // connect resolves after setupComplete, so the greeting cannot race setup.
      session.sendRealtimeInput({ text: `A new caller has just connected. Start speaking now: briefly introduce yourself as ${activeAgent.name}, the AI assistant for your business, and ask how you can help. Keep your greeting to one or two short sentences. Do not wait for the caller to speak.` });
      const inputSource = inputContext.createMediaStreamSource(stream);
      const processor = inputContext.createScriptProcessor(2048, 1, 1);
      inputSourceRef.current = inputSource;
      processorRef.current = processor;
      processor.onaudioprocess = (event) => {
        if (!isCurrent()) return;
        const audio = createAudioBlob(event.inputBuffer.getChannelData(0));
        audio.mimeType = `audio/pcm;rate=${inputContext.sampleRate}`;
        session.sendRealtimeInput({ audio });
      };
      inputSource.connect(processor);
      processor.connect(inputContext.destination);
      setConnectionState('connected');
    } catch (error) {
      if (!isCurrent()) return;
      void stopSession(false);
      setConnectionState('error');
      setErrorMessage(error instanceof Error && error.name === 'NotAllowedError'
        ? 'Microphone access is needed for a call. Allow it in your browser settings, then try again.'
        : error instanceof Error ? error.message : 'Could not start the live voice session.');
    }
  };

  const toggleLiveCall = () => {
    if (connectionState === 'connected' || connectionState === 'connecting') {
      void stopSession();
      return;
    }
    void startSession();
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="site-shell">
      <WaveTrail />
      <header className="site-header">
        <a className="brand header-brand" href="#top" aria-label="RESERVOLUTION AI voice receptionist home" onClick={closeMenu}>
          <span className="header-brand-mark"><AnimatedLogo /></span>
          <span className="header-brand-subline">AI voice receptionist</span>
        </a>
        <nav className={menuOpen ? 'nav-open' : ''} aria-label="Main navigation">
          <a href="#demo" onClick={closeMenu}>Hear it work</a>
          <a href="#how" onClick={closeMenu}>How it works</a>
          <a href="#pricing" onClick={closeMenu}>Pricing</a>
          <a href="#faq" onClick={closeMenu}>FAQ</a>
          <a className="nav-cta" href="#contact" onClick={closeMenu}>Talk to us <ArrowUpRight size={16} /></a>
        </nav>
        <button className="menu-button" onClick={() => setMenuOpen((open) => !open)} aria-label={menuOpen ? 'Close navigation' : 'Open navigation'} aria-expanded={menuOpen}>
          {menuOpen ? <X /> : <Menu />}
        </button>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy reveal-one">
            <div className="eyebrow"><span /> Your always-on AI receptionist</div>
            <h1>Your business calls.<br /><em>We answer.</em></h1>
            <p>Bookings, questions, and new leads, handled in a natural conversation. Your team stays focused. Every caller gets an answer.</p>
            <div className="hero-channels" aria-label="Available service channels">
              <span className="hero-channels-label">Deploy in two ways</span>
              <div>
                <span className="hero-channel"><PhoneCall size={20} /><span><strong>Phone call agent</strong><small>Answers your business line</small></span></span>
                <span className="hero-channel"><Mic size={20} /><span><strong>Website voice chat</strong><small>Talk directly in the browser</small></span></span>
              </div>
            </div>
            <div className="hero-actions">
              <a className="text-link" href="#demo">Choose an agent and try a call <ArrowRight size={18} /></a>
              <a className="text-link secondary-link" href="#pricing">See pricing <ArrowUpRight size={16} /></a>
            </div>
            <aside className="hero-system-note" aria-label="System options">
              <div>
                <span>Your systems, or ours</span>
                <p>Connect the agent to your reservation, calendar, CRM, or inventory tools. Restaurants can also use our advanced scheduling system, built to work with the agent from day one.</p>
              </div>
              <a href="/restaurants/">Explore restaurant scheduling <ArrowUpRight size={16} /></a>
            </aside>
          </div>

          <section className="hero-demo reveal-two" id="demo" aria-labelledby="demo-heading">
          <div className="scheduler-reaction-stage" aria-live="polite" aria-atomic="true">
            {schedulerReceipt && (
              <div className={`scheduler-reaction scheduler-${schedulerReceipt.phase}`} key={schedulerReceipt.id} role="status">
                <span className="scheduler-reaction-icon">{['checking', 'saving'].includes(schedulerReceipt.phase) ? <Clock3 size={20} /> : <CalendarCheck size={20} />}</span>
                <span><small>Demo scheduler</small><strong>{schedulerReceipt.outcome}</strong><em>{['checking', 'saving'].includes(schedulerReceipt.phase) ? <Loader2 className="spin" size={13} /> : <Check size={13} />}{schedulerReceipt.status}</em></span>
              </div>
            )}
          </div>
          <div className="demo-heading"><h2 id="demo-heading">Hear it for yourself.</h2><span>Live AI demo</span></div>
          <div className="agent-tabs" role="tablist" aria-label="Voice agent scenarios">
            {agents.map((agent, index) => (
              <button
                key={agent.id}
                id={`agent-tab-${agent.id}`}
                ref={(element) => { tabRefs.current[index] = element; }}
                role="tab"
                tabIndex={index === activeIndex ? 0 : -1}
                aria-selected={index === activeIndex}
                aria-controls="agent-panel"
                className={index === activeIndex ? 'active' : ''}
                onClick={() => selectAgent(index)}
                onKeyDown={(event) => {
                  const next = event.key === 'ArrowRight' ? (index + 1) % agents.length
                    : event.key === 'ArrowLeft' ? (index + agents.length - 1) % agents.length
                    : event.key === 'Home' ? 0 : event.key === 'End' ? agents.length - 1 : null;
                  if (next === null) return;
                  event.preventDefault();
                  selectAgent(next);
                  tabRefs.current[next]?.focus();
                }}
              >
                <img src={agent.thumbnail} alt="" width="40" height="40" decoding="async" />
                <span><strong>{agent.name}</strong><small>{agent.industry}</small></span>
              </button>
            ))}
          </div>

          <article className="agent-experience" id="agent-panel" role="tabpanel" aria-labelledby={`agent-tab-${activeAgent.id}`} key={activeAgent.id}>
            <div className="agent-intro"
              onPointerDown={(event) => { if (event.pointerType === 'touch') swipeStartRef.current = { x: event.clientX, y: event.clientY }; }}
              onPointerCancel={() => { swipeStartRef.current = null; }}
              onPointerUp={(event) => {
                const start = swipeStartRef.current;
                swipeStartRef.current = null;
                if (!start) return;
                const dx = event.clientX - start.x;
                const dy = event.clientY - start.y;
                if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) moveAgent(dx < 0 ? 1 : -1);
              }}>
              <img className="agent-photo" src={activeAgent.image} alt={activeAgent.alt} width="160" height="180" fetchPriority="high" decoding="async" draggable={false} />
              <div><span className="agent-specialty">{activeAgent.role}</span><h3>Meet {activeAgent.name}.</h3><p>{activeAgent.title}</p></div>
            </div>
            <div className="agent-story">
              <p className="demo-prompt">Try asking: “{activeAgent.transcript[0].text}”</p>
              <div className={`voice-player ${connectionState === 'connected' ? 'playing' : ''} ${connectionState === 'error' ? 'voice-error' : ''}`}>
                <button onClick={toggleLiveCall} aria-label={connectionState === 'connecting' ? 'Cancel' : connectionState === 'connected' ? 'End call' : `Talk to ${activeAgent.name}`}>
                  {connectionState === 'connecting' ? <Loader2 className="spin" size={21} /> : connectionState === 'connected' ? <PhoneOff size={21} /> : <Mic size={21} />}
                  <span>{connectionState === 'connecting' ? 'Cancel' : connectionState === 'connected' ? 'End call' : `Talk to ${activeAgent.name}`}</span>
                </button>
                <VoiceWaveform analyser={outputAnalyserRef.current} active={connectionState === 'connected'} />
              </div>
              <p className="call-guidance" role="status">{connectionState === 'connecting' ? 'Allow microphone access. Your agent will say hello first.' : connectionState === 'connected' ? `${activeAgent.name} is on the line. Speak naturally.` : 'Tap the mic. Allow your microphone. We’ll say hello first.'}</p>
              {errorMessage && <p className="voice-error-message" role="alert"><AlertCircle size={16} /> {errorMessage}</p>}
              {(liveTranscript.caller || liveTranscript.agent) && (
                <div className="live-transcript" aria-live="polite">
                  <span>Live transcript</span>
                  {liveTranscript.caller && <p className="transcript-turn" key={`caller-${liveTranscript.callerTurn}`}><b>You:</b> {liveTranscript.caller}</p>}
                  {liveTranscript.agent && <p className="transcript-turn" key={`agent-${liveTranscript.agentTurn}`}><b>{activeAgent.name}:</b> {liveTranscript.agent}</p>}
                </div>
              )}

              <details className="sample-conversation">
                <summary>Read a sample conversation <ChevronDown size={16} /></summary>
              <div className="transcript" aria-label={`${activeAgent.name} demo transcript`}>
                {activeAgent.transcript.map((line, index) => (
                  <div className={line.speaker === 'Agent' ? 'agent-line' : ''} key={`${line.speaker}-${index}`}>
                    <span>{line.speaker === 'Agent' ? activeAgent.name : line.speaker}</span>
                    <p>{line.text}</p>
                  </div>
                ))}
              </div>
              <div className="demo-outcome"><Check size={17} /> Example: {activeAgent.outcome}</div>
              </details>
            </div>
          </article>
          <p className="demo-note">Sample businesses and clinic. No real bookings or medical advice are provided.</p>
          </section>
        </section>

        <section className="confidence-strip" aria-label="Core service capabilities">
          <Capability icon={<Phone size={20} />} title="Every caller">answered without a queue</Capability>
          <Capability icon={<CalendarCheck size={20} />} title="Real actions">not just messages</Capability>
          <Capability icon={<Mic size={20} />} title="Phone + website voice">one agent, two channels</Capability>
          <Capability icon={<ShieldCheck size={20} />} title="Human handoff">on your terms</Capability>
        </section>

        <section className="how-section" id="how">
          <div className="section-heading compact">
            <span className="section-index">What happens after hello</span>
            <h2>AI phone answering<br />built for the rush.</h2>
          </div>
          <div className="service-flow">
            <article>
              <PhoneCall />
              <h3>Your customer calls</h3>
              <p>RESERVOLUTION answers instantly on your existing call flow, including peak periods and after-hours.</p>
            </article>
            <article>
              <MessageSquareText />
              <h3>The intent becomes clear</h3>
              <p>The agent understands interruptions, follow-up questions, names, times, products, reservations, and property requests.</p>
            </article>
            <article>
              <CalendarCheck />
              <h3>The task gets done</h3>
              <p>It answers, captures, books, routes, or escalates based on the rules you approve.</p>
            </article>
            <article>
              <Sparkles />
              <h3>Your team keeps the context</h3>
              <p>A clear summary and transcript make the next step visible without replaying the call.</p>
            </article>
          </div>
        </section>

        <section className="feature-section">
          <div className="feature-intro">
            <span className="section-index">03 / Designed for real business</span>
            <h2>Not another phone tree.</h2>
            <p>A useful voice agent has to understand your operation, finish the routine work, and know when to step aside.</p>
          </div>
          <div className="feature-mosaic">
            <article className="feature-primary">
              <div className="feature-copy">
                <span>Natural conversation</span>
                <h3>Customers talk the way they always do.</h3>
                <p>No button menus or rigid phrases. The agent handles corrections, interruptions, accents, and follow-up questions in one flow.</p>
              </div>
              <div className="conversation-snippet">
                <div><b>Caller</b><p>Actually, could we make that Thursday after 4?</p></div>
                <div><b>RESERVOLUTION</b><p>Of course. I have a 4:30 appointment available.</p></div>
              </div>
            </article>
            <article className="feature-actions">
              <CalendarCheck />
              <span>Action, not voicemail</span>
              <h3>The call ends with an outcome.</h3>
              <ul>
                <li><Check /> Book a reservation, viewing, or clinic visit</li>
                <li><Check /> Check stock or qualify a lead</li>
                <li><Check /> Answer product and policy questions</li>
              </ul>
            </article>
            <article className="feature-handoff">
              <ShieldCheck />
              <span>Controlled escalation</span>
              <h3>Your team decides when a person steps in.</h3>
              <div className="handoff-status"><span className="live-dot" /><div><strong>VIP caller detected</strong><small>Transferring with context</small></div><ArrowUpRight /></div>
            </article>
          </div>
        </section>

        <section className="compare-section" id="compare">
          <div className="section-heading compact">
            <span className="section-index">04 / The useful difference</span>
            <h2>More than an<br />answering service.</h2>
            <p>Built to resolve common business calls while preserving the human judgment that customers and teams depend on.</p>
          </div>
          <div className="comparison-wrap">
            <table>
              <thead>
                <tr><th>Call experience</th><th>Voicemail</th><th>Traditional service</th><th>RESERVOLUTION</th></tr>
              </thead>
              <tbody>
                <tr><th scope="row">Answers every simultaneous call</th><td data-label="Voicemail">Never</td><td data-label="Traditional service">Capacity dependent</td><td data-label="RESERVOLUTION"><Check /> Yes</td></tr>
                <tr><th scope="row">Completes routine customer tasks</th><td data-label="Voicemail">No</td><td data-label="Traditional service">Varies</td><td data-label="RESERVOLUTION"><Check /> Yes</td></tr>
                <tr><th scope="row">Uses your approved knowledge</th><td data-label="Voicemail">No</td><td data-label="Traditional service">Script dependent</td><td data-label="RESERVOLUTION"><Check /> Yes</td></tr>
                <tr><th scope="row">Available after hours</th><td data-label="Voicemail">Message only</td><td data-label="Traditional service">Plan dependent</td><td data-label="RESERVOLUTION"><Check /> Yes</td></tr>
                <tr><th scope="row">Hands off with transcript and context</th><td data-label="Voicemail">No</td><td data-label="Traditional service">Varies</td><td data-label="RESERVOLUTION"><Check /> Yes</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="systems-section">
          <div className="systems-copy">
            <span className="section-index">05 / Fits the way you work</span>
            <h2>Your systems stay<br />at the centre.</h2>
            <p>We map the agent around the tools, policies, and escalation paths your team already trusts.</p>
          </div>
          <div className="system-orbit" aria-label="Supported workflow categories">
            <div className="orbit-core"><img src="/reservolution-mark.webp" alt="" width="256" height="256" loading="lazy" decoding="async" /></div>
            <span className="orbit-item orbit-one"><CalendarCheck /> Booking systems</span>
            <span className="orbit-item orbit-two"><Clock3 /> Business hours</span>
            <span className="orbit-item orbit-three"><MessageSquareText /> SMS follow-up</span>
            <span className="orbit-item orbit-four"><ShieldCheck /> Human handoff</span>
          </div>
        </section>

        <section className="voice-pricing-section" id="pricing" aria-labelledby="voice-pricing-heading">
          <div className="voice-pricing-intro">
            <span className="section-index">06 / Clear pricing</span>
            <h2 id="voice-pricing-heading">The agent is fixed.<br /><em>The calls scale.</em></h2>
            <p>Founding prices are available to our first 10 customers and locked for 12 months. The monthly plan covers the agent and its workflow; connected-minute pricing keeps lighter users from subsidising busy operations.</p>
          </div>

          <div className="voice-pricing-plans">
            <article className="voice-price-plan">
              <div className="voice-price-plan-heading">
                <span>Voice Receptionist</span>
                <p>For retail, property, clinics, and service teams that need every caller answered.</p>
              </div>
              <div className="voice-launch-lock"><span>Founding price</span><small>First 10 customers · locked for 12 months</small></div>
              <div className="voice-price"><small>€</small><strong>99</strong><span>per agent<br />per month<br /><del>Normally €149</del></span></div>
              <div className="voice-usage-rate"><strong>+ €0.25</strong><span>per connected minute</span></div>
              <ul>
                <li><Check /> Answers approved questions</li>
                <li><Check /> Captures leads and messages</li>
                <li><Check /> Routes calls with context</li>
                <li><Check /> Transcripts and call summaries</li>
              </ul>
              <div className="voice-plan-footer">
                <span>One-time setup from €199</span>
                <a className="text-link" href="#contact">Claim founding price <ArrowRight size={17} /></a>
              </div>
            </article>

            <article className="voice-price-plan voice-price-featured">
              <div className="voice-price-plan-heading">
                <span>Voice Operations</span>
                <p>For agents that complete work across calendars, customer systems, and multi-step call flows.</p>
              </div>
              <div className="voice-launch-lock"><span>Founding price</span><small>First 10 customers · locked for 12 months</small></div>
              <div className="voice-price"><small>€</small><strong>249</strong><span>per agent<br />per month<br /><del>Normally €299</del></span></div>
              <div className="voice-usage-rate"><strong>+ €0.25</strong><span>per connected minute</span></div>
              <ul>
                <li><Check /> Everything in Voice Receptionist</li>
                <li><Check /> Calendar, CRM, and webhook actions</li>
                <li><Check /> Appointments and lead qualification</li>
                <li><Check /> Multiple workflows and transfers</li>
              </ul>
              <div className="voice-plan-footer">
                <span>One-time setup from €499</span>
                <a className="text-link" href="#contact">Claim founding price <ArrowRight size={17} /></a>
              </div>
            </article>
          </div>

          <div className="voice-price-examples" aria-label="Example monthly voice-agent costs">
            <span>Monthly examples</span>
            <div><strong>€174</strong><small>Receptionist + 300 minutes</small></div>
            <div><strong>€499</strong><small>Operations + 1,000 minutes</small></div>
            <div><strong>€874</strong><small>Operations + 2,500 minutes</small></div>
          </div>

          <aside className="voice-enterprise">
            <div>
              <span>Enterprise</span>
              <h3>From €599 per month, plus tailored usage.</h3>
            </div>
            <p>For multi-location operations, custom integrations, higher concurrency, dedicated support, or workflows that need additional governance.</p>
            <a className="button button-secondary" href="#contact">Talk to a voice specialist <ArrowUpRight size={17} /></a>
          </aside>

          <div className="voice-regulated-note">
            <ShieldCheck size={23} />
            <div><strong>Healthcare and regulated workflows are quoted separately.</strong><p>Security, data retention, regional hosting, and contractual requirements are confirmed before we recommend a configuration or price.</p></div>
            <a className="text-link" href="#contact">Discuss requirements <ArrowRight size={17} /></a>
          </div>

          <p className="voice-pricing-note">Connected-minute rates cover the standard voice, AI processing, and phone workflow. International or premium routing, specialist models, and unusually complex integrations may cost more. We confirm the complete estimate before activation. Setup fees and VAT are excluded from the monthly examples.</p>
        </section>

        <section className="faq-section" id="faq">
          <div className="faq-title">
            <span className="section-index">07 / Questions operators ask</span>
            <h2>Before you<br />hand over hello.</h2>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <details key={faq.question} open={index === 0}>
                <summary><span>0{index + 1}</span>{faq.question}<ChevronDown /></summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <ContactForm />
      </main>

      <footer>
        <a className="brand footer-brand" href="#top" aria-label="Back to top"><img src="/reservolution-logo.webp" alt="RESERVOLUTION" width="640" height="213" loading="lazy" decoding="async" /></a>
        <p>AI voice reception for modern business.</p>
        <a href="#top">Back to top <ArrowUpRight size={15} /></a>
      </footer>
    </div>
  );
};

const RestaurantSchedulingPage: React.FC = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'AI Restaurant Phone Answering & Reservations | RESERVOLUTION';
    window.scrollTo(0, 0);
    return () => { document.title = previousTitle; };
  }, []);

  return (
    <div className="site-shell restaurant-shell">
      <WaveTrail />
      <header className="site-header restaurant-header">
        <a className="brand header-brand" href="/" aria-label="RESERVOLUTION AI voice receptionist home">
          <span className="header-brand-mark"><AnimatedLogo /></span>
          <span className="header-brand-subline">AI voice receptionist</span>
        </a>
        <nav aria-label="Restaurant page navigation">
          <a className="restaurant-nav-home" href="/">Voice agents</a>
          <a className="restaurant-nav-pricing" href="#pricing">Pricing</a>
          <a className="nav-cta" href="#contact">Start setup <ArrowUpRight size={16} /></a>
        </nav>
      </header>

      <main id="top" className="restaurant-page">
        <section className="restaurant-product-hero">
          <div className="restaurant-hero-copy reveal-one">
            <div className="eyebrow"><span /> RESERVOLUTION for restaurants</div>
            <h1>The booking system that <em>answers the phone.</em></h1>
            <p>Use our dedicated restaurant scheduling system with your voice agent, or connect the agent to the reservation platform you already use.</p>
            <div className="restaurant-hero-actions">
              <a className="button button-primary" href="/#demo"><PhoneCall size={18} /> Try a reservation call</a>
              <a className="text-link secondary-link" href="#pricing">See pricing <ArrowRight size={17} /></a>
            </div>
            <div className="restaurant-proof-line">
              <span><Check size={14} /> Live availability</span>
              <span><Check size={14} /> Booking rules</span>
              <span><Check size={14} /> Guest context</span>
              <span><Check size={14} /> From €249 per location</span>
            </div>
          </div>

          <div className="schedule-preview reveal-two" aria-label="Sample restaurant scheduling system view">
            <div className="schedule-preview-top">
              <div><small>Sample schedule</small><strong>Friday service</strong></div>
              <span><i /> Synced</span>
            </div>
            <div className="availability-label"><span>Live availability</span><small>Party of 4</small></div>
            <div className="availability-slots">
              <div><span>6:30</span><small>Full</small></div>
              <div className="slot-open"><span>7:15</span><small>Available</small></div>
              <div className="slot-open"><span>7:45</span><small>Available</small></div>
            </div>
            <article className="sample-booking">
              <div className="booking-agent"><span className="live-dot" /><small>Mara is booking</small></div>
              <div className="booking-main">
                <div><strong>Jordan Lee</strong><span>4 guests</span></div>
                <b>7:15 PM</b>
              </div>
              <p>Window table preferred <span>Gluten-free note</span></p>
            </article>
            <div className="schedule-status">
              <span><Check size={15} /> Availability checked</span>
              <span><CalendarCheck size={15} /> Booking saved</span>
            </div>
          </div>
        </section>

        <section className="restaurant-ways" id="ways">
          <div className="restaurant-section-heading">
            <span className="section-index">Choose your setup</span>
            <h2>One agent.<br />Two ways to run it.</h2>
            <p>Your operation stays in control. RESERVOLUTION can work with the systems your team knows, or provide the scheduling layer too.</p>
          </div>
          <div className="setup-options">
            <article>
              <span className="setup-icon"><ArrowRight size={21} /></span>
              <div>
                <small>Connect</small>
                <h3>Keep your current system</h3>
                <p>Your existing reservation, calendar, CRM, or inventory platform remains the source of truth. The agent checks it and takes action during the call.</p>
              </div>
              <strong>Existing tools</strong>
            </article>
            <article>
              <span className="setup-icon setup-icon-gold"><CalendarCheck size={21} /></span>
              <div>
                <small>Complete</small>
                <h3>Use RESERVOLUTION scheduling</h3>
                <p>Give the agent and your team one shared view of availability, booking rules, guest notes, and call confirmations from the start.</p>
              </div>
              <strong>Agent + scheduler</strong>
            </article>
          </div>
        </section>

        <section className="restaurant-workflow">
          <div className="restaurant-section-heading compact">
            <span className="section-index">Built for service</span>
            <h2>From ringing<br />to reserved.</h2>
          </div>
          <div className="restaurant-workflow-list">
            <article>
              <Clock3 />
              <h3>Availability stays current</h3>
              <p>The agent checks the business system only when the guest is ready to choose a time.</p>
            </article>
            <article>
              <PhoneCall />
              <h3>Calls become bookings</h3>
              <p>Dates, party size, preferences, and contact details move into a confirmed reservation.</p>
            </article>
            <article>
              <MessageSquareText />
              <h3>Your team keeps context</h3>
              <p>Guest notes and call details arrive with the booking, ready for the host team to use.</p>
            </article>
          </div>
        </section>

        <section className="restaurant-pricing" id="pricing" aria-labelledby="restaurant-pricing-heading">
          <div className="restaurant-pricing-intro">
            <span className="section-index">Straightforward pricing</span>
            <h2 id="restaurant-pricing-heading">Know where the price starts.</h2>
            <p>Begin with one restaurant. We confirm phone availability, expected voice usage, and the full monthly estimate before you activate.</p>
          </div>
          <div className="restaurant-pricing-layout">
            <article className="restaurant-price-plan">
              <div className="price-plan-top">
                <div><span>Restaurant launch</span><strong>Scheduling + voice agent</strong></div>
                <ShieldCheck aria-hidden="true" />
              </div>
              <div className="restaurant-price"><small>From</small><strong>€249</strong><span>per location<br />per month</span></div>
              <ul>
                <li><Check size={17} /> Restaurant scheduling and availability</li>
                <li><Check size={17} /> AI reservation calls and guest questions</li>
                <li><Check size={17} /> Booking rules, notes, and confirmations</li>
                <li><Check size={17} /> Self-service setup and test bookings</li>
                <li><Check size={17} /> Assisted phone activation and live check</li>
              </ul>
              <a className="button button-primary" href="#contact">Start my restaurant setup <ArrowUpRight size={17} /></a>
              <p className="restaurant-price-note">Voice usage and phone costs vary by country and monthly call volume. Your complete estimate is agreed before activation. Prices exclude VAT where applicable.</p>
            </article>
            <aside className="restaurant-price-aside">
              <span>Existing system or restaurant group?</span>
              <h3>Fit the agent around your operation.</h3>
              <p>Keep a supported reservation platform, connect more than one location, or scope a custom call flow with us.</p>
              <ul>
                <li>Compatibility review</li>
                <li>Multi-location rollout</li>
                <li>Custom integrations and handoffs</li>
              </ul>
              <a className="text-link secondary-link" href="#contact">Book a restaurant demo <ArrowRight size={17} /></a>
            </aside>
          </div>
        </section>

        <section className="restaurant-faq" id="questions" aria-labelledby="restaurant-faq-heading">
          <div className="restaurant-faq-intro">
            <span className="section-index">Before you start</span>
            <h2 id="restaurant-faq-heading">The practical questions.</h2>
          </div>
          <div className="restaurant-faq-list">
            <details>
              <summary>Can I keep my current reservation system?<ChevronDown /></summary>
              <p>Yes, when your platform is supported. You can also use RESERVOLUTION scheduling as the shared source of availability, booking rules, and guest notes. We confirm compatibility before activation.</p>
            </details>
            <details>
              <summary>Can I set up the restaurant myself?<ChevronDown /></summary>
              <p>You can configure your restaurant, availability, booking rules, and test reservations through onboarding. Our team currently completes the phone-number connection and final live check with you.</p>
            </details>
            <details>
              <summary>What will I pay each month?<ChevronDown /></summary>
              <p>Restaurant plans start at €249 per location each month. Voice usage and phone costs depend on your country and call volume, so we provide the complete monthly estimate before you activate.</p>
            </details>
            <details>
              <summary>Can I hear the agent before I commit?<ChevronDown /></summary>
              <p>Yes. Try Mara in the live reservation demo, or request a restaurant-specific walkthrough before starting your setup.</p>
            </details>
          </div>
        </section>

        <ContactForm context="restaurant" />
      </main>

      <footer>
        <a className="brand footer-brand" href="/" aria-label="RESERVOLUTION home"><img src="/reservolution-logo.webp" alt="RESERVOLUTION" width="640" height="213" loading="lazy" decoding="async" /></a>
        <p>AI voice reception for modern business.</p>
        <a href="/">Voice agent overview <ArrowUpRight size={15} /></a>
      </footer>
    </div>
  );
};

const App: React.FC<{ initialRoute?: 'home' | 'restaurants' }> = ({ initialRoute }) => initialRoute === 'restaurants'
  || (typeof window !== 'undefined' && window.location.pathname.startsWith('/restaurants'))
  || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('page') === 'restaurants')
  ? <RestaurantSchedulingPage />
  : <HomePage />;

export default App;
